import { RedeemCodeResponse, BundleItem, InAppPurchase } from 'mytours-read-api-typescript'
import { reducerWithInitialState } from 'typescript-fsa-reducers/dist'

import * as apiActions from '../api/actions'
import { isHttpClientError } from '../api/errors'
import { expireAtFromHours } from '../api/functions'
import { BundleFetchFailures, BundleProducts, CollectionWebPrices, RedeemedBundleEntry, RedeemedBundles } from './types'

export interface StoreState {
	readonly redeemedBundles: RedeemedBundles
	readonly bundleProducts: BundleProducts
	/** Bundles whose own fetch failed, so the metadata sync can replay it and a gate can say why it cannot
	 * name one. Optional: older persisted state and test fixtures predate it. */
	readonly bundleFetchFailed?: BundleFetchFailures
	/** Optional for the same reason, and empty until the first catalogue arrives. */
	readonly collectionWebPrices?: CollectionWebPrices
}

export const INITIAL_STATE: StoreState = {
	redeemedBundles: {},
	bundleProducts: {},
}

// Upsert that bails without a new state reference when the entry is unchanged — re-validation
// runs on every boot, periodic sync and screen focus, and pointless new references would
// re-render every bundles consumer (SectionLinkGroup, Purchased Content) each time.
const setBundleEntry = (state: StoreState, id: number, entry: RedeemedBundleEntry): StoreState => {
	const prev = state.redeemedBundles[id]
	if (
		prev &&
		prev.hasUnlockedBundle === entry.hasUnlockedBundle &&
		prev.redeemingCoupon === entry.redeemingCoupon &&
		prev.expireAt === entry.expireAt &&
		prev.redeemCouponError === entry.redeemCouponError &&
		prev.couponUsedToUnlock === entry.couponUsedToUnlock &&
		prev.items === entry.items &&
		prev.addedAt === entry.addedAt
	) {
		return state
	}
	return { ...state, redeemedBundles: { ...state.redeemedBundles, [id]: entry } }
}

// A failed (re-)redeem records the error for the dialog but must not destroy an existing unlock:
// scanning a stale QR code for an already-unlocked bundle, or a transport failure mid-redeem,
// leaves the bundle exactly as unlocked as it was before the attempt.
const failRedeem = (state: StoreState, id: number, error: Error | undefined): StoreState => {
	const prev = state.redeemedBundles[id]
	return setBundleEntry(state, id, {
		...prev,
		hasUnlockedBundle: prev?.hasUnlockedBundle ?? false,
		redeemingCoupon: false,
		redeemCouponError: error,
	})
}

export const reducer = reducerWithInitialState(INITIAL_STATE)
	// ---- redeem bundle coupon ----
	.case(apiActions.redeemBundleCoupon.started, (state, payload): StoreState => {
		const prev = state.redeemedBundles[payload.id]
		return setBundleEntry(state, payload.id, {
			...prev,
			hasUnlockedBundle: prev?.hasUnlockedBundle ?? false,
			redeemingCoupon: true,
			redeemCouponError: undefined,
		})
	})
	.case(apiActions.redeemBundleCoupon.done, (state, payload): StoreState => {
		const prev = state.redeemedBundles[payload.params.id]
		switch (payload.result.status) {
			case RedeemCodeResponse.StatusEnum.Valid:
				return setBundleEntry(state, payload.params.id, {
					redeemingCoupon: false,
					hasUnlockedBundle: true,
					expireAt: expireAtFromHours(payload.result.expire_after),
					couponUsedToUnlock: payload.params.coupon,
					items: payload.result.items,
					// Fresh redeem stamps now; preserve the original on a re-redeem.
					addedAt: prev?.addedAt ?? Date.now(),
				})
			case RedeemCodeResponse.StatusEnum.Invalid:
				return failRedeem(state, payload.params.id, Error(apiActions.ERROR_MSG_INVALID_COUPON))
			case RedeemCodeResponse.StatusEnum.Expired:
				return failRedeem(state, payload.params.id, Error(apiActions.ERROR_MSG_EXPIRED_COUPON))
			case RedeemCodeResponse.StatusEnum.MaxRedemptions:
				return failRedeem(state, payload.params.id, Error(apiActions.ERROR_MSG_MAX_REDEMPTIONS_COUPON))
			case RedeemCodeResponse.StatusEnum.RequiresEmail:
				return failRedeem(state, payload.params.id, Error(apiActions.ERROR_MSG_REQUIRES_EMAIL_COUPON))
			default:
				return failRedeem(state, payload.params.id, undefined)
		}
	})
	.case(apiActions.redeemBundleCoupon.failed, (state, payload): StoreState => {
		return failRedeem(state, payload.params.id, payload.error)
	})
	// ---- validate bundle coupon (periodic re-validation) ----
	//
	// A bundle can have several persisted codes over time (replacement codes, in-place re-saves),
	// and in-session redeems race the boot/sync revalidation loop — so no ordering of `.started`
	// dispatches reliably identifies the newest code. The race-free invariant used here instead:
	// `.started` only seeds a missing entry, a Valid result always (re-)establishes the unlock and
	// records its coupon, and only a result for the RECORDED coupon may revoke. Any interleaving
	// then ends unlocked whenever at least one persisted code is valid (at worst with a brief
	// drop-and-re-add within one revalidation cycle).
	.case(apiActions.validateBundleCoupon.started, (state, payload): StoreState => {
		const prev = state.redeemedBundles[payload.id]
		if (prev) {
			// Revalidating a known bundle: leave the entry — including any in-flight redeem
			// state and its recorded error — to the result handlers.
			return state
		}
		// Seed the unlock optimistically: `.started` only ever fires for codes that were
		// successfully redeemed (boot revalidation of persisted disk codes, or screen-focus
		// revalidation of an already-unlocked bundle). The slice is wiped on every reload, so
		// without seeding an offline boot's transport-failed validation would have no prior
		// unlock to preserve and redeemed bundles would re-lock.
		return setBundleEntry(state, payload.id, {
			hasUnlockedBundle: true,
			redeemingCoupon: false,
			expireAt: payload.expireAt,
			couponUsedToUnlock: payload.coupon,
			addedAt: payload.addedAt,
		})
	})
	.case(apiActions.validateBundleCoupon.done, (state, payload): StoreState => {
		const prev = state.redeemedBundles[payload.params.id]
		const { status } = payload.result
		let valid = false
		let error: Error | undefined
		let couponUsedToUnlock: string | undefined
		let expireAt: number | undefined
		// Treat MaxRedemptions as valid when re-validating: the code already unlocked once.
		if (status === RedeemCodeResponse.StatusEnum.Valid || status === RedeemCodeResponse.StatusEnum.MaxRedemptions) {
			valid = true
			couponUsedToUnlock = payload.params.coupon
			expireAt = payload.params.expireAt
		} else if (status === RedeemCodeResponse.StatusEnum.Invalid || status === RedeemCodeResponse.StatusEnum.Expired) {
			if (prev?.couponUsedToUnlock && prev.couponUsedToUnlock !== payload.params.coupon) {
				// Only the recorded coupon may revoke — a stale code's Invalid can't re-lock an
				// unlock established by another code.
				return state
			}
			error = Error(
				status === RedeemCodeResponse.StatusEnum.Invalid
					? apiActions.ERROR_MSG_INVALID_COUPON
					: apiActions.ERROR_MSG_EXPIRED_COUPON,
			)
		} else {
			// Any other (non-definitive) status — e.g. RequiresEmail returned because the
			// re-validate call sends no email — must NOT silently revoke a working unlock.
			// Only Invalid/Expired are treated as definitive negatives; preserve otherwise.
			valid = prev?.hasUnlockedBundle ?? false
			couponUsedToUnlock = prev?.couponUsedToUnlock
			expireAt = prev?.expireAt
		}
		return setBundleEntry(state, payload.params.id, {
			// A background validation must not clobber an in-flight redeem's flag or its
			// recorded error — CodeRedeem reads both to report ITS attempt's outcome. Only a
			// definitive validation error of the recorded coupon replaces the error.
			redeemingCoupon: prev?.redeemingCoupon ?? false,
			hasUnlockedBundle: valid,
			expireAt,
			redeemCouponError: error ?? prev?.redeemCouponError,
			couponUsedToUnlock,
			items: prev?.items,
			// Carry the disk code's redeem timestamp (boot revalidation passes it) onto the entry.
			addedAt: payload.params.addedAt ?? prev?.addedAt,
		})
	})
	.case(apiActions.validateBundleCoupon.failed, (state, payload): StoreState => {
		// Optimistic offline: a transport failure or 5xx is transient and leaves the entry —
		// including the `.started` seed — untouched. Only a definitive 4xx rejection (the
		// backend deleted or revoked the code → 404/410) re-locks, and like `.done` it may not
		// re-lock an unlock recorded for a different (newer) coupon.
		if (!isHttpClientError(payload.error)) {
			return state
		}
		const prev = state.redeemedBundles[payload.params.id]
		if (prev?.couponUsedToUnlock && prev.couponUsedToUnlock !== payload.params.coupon) {
			return state
		}
		return setBundleEntry(state, payload.params.id, {
			redeemingCoupon: prev?.redeemingCoupon ?? false,
			hasUnlockedBundle: false,
			redeemCouponError: payload.error,
			items: prev?.items,
		})
	})
	// ---- reconcile redeemed bundles' items against a freshly-fetched IAP catalogue ----
	// Add/remove/rename/cover-image changes propagate without a re-redeem. Bundles missing
	// from a *complete* catalogue are pruned entirely (but not when the page was truncated).
	.case(apiActions.fetchInAppPurchases.done, (state, payload): StoreState => {
		const iaps = payload.result.data
		// The IAP fetch treats an empty catalogue as a hard error elsewhere; guard here too.
		if (!iaps || iaps.length === 0) {
			return state
		}

		// We only fetch page 1 (with a large per_page). If the catalogue is paginated beyond
		// that, this page is incomplete — a redeemed bundle could live on a later page, so we
		// must NOT prune the ones missing here (only reconcile the ones we did see).
		const catalogueIsComplete = (payload.result.meta?.pages ?? 1) <= 1

		const bundleItemsById = new Map<number, BundleItem[]>()
		// Capture purchasability (active + web_price) so the redeem dialog can offer a "Buy bundle" button
		// for bundles sold via Stripe, even before redemption.
		//
		// Updated in place, unlike the redeemedBundles reconciliation below: fetchBundle writes here too, and
		// rebuilding would drop the bundles this catalogue cannot name. Nothing needs pruning either — the
		// catalogue is `Product.kept`, so a bundle taken off sale still arrives, carrying `active: false`.
		const bundleProducts: BundleProducts = { ...state.bundleProducts }
		// Rebuilt rather than merged: unlike a bundle, a collection's price only ever comes from here.
		const collectionWebPrices: CollectionWebPrices = {}
		iaps.forEach(iap => {
			if (iap.bundle?.id !== undefined) {
				bundleItemsById.set(iap.bundle.id, iap.bundle.items || [])
				bundleProducts[iap.bundle.id] = {
					active: iap.active ?? false,
					webPrice: iap.web_price,
					name: iap.bundle.name,
					items: iap.bundle.items,
				}
			} else if (
				iap.item_type === InAppPurchase.ItemTypeEnum.Collection &&
				iap.item_id !== undefined &&
				iap.active &&
				iap.web_price?.amount
			) {
				collectionWebPrices[iap.item_id] = iap.web_price
			}
		})

		const next: RedeemedBundles = {}
		Object.keys(state.redeemedBundles).forEach(idStr => {
			const existing = state.redeemedBundles[idStr]
			if (!existing) {
				return
			}
			const items = bundleItemsById.get(Number(idStr))
			if (items !== undefined) {
				next[idStr] = { ...existing, items }
			} else if (!catalogueIsComplete) {
				// Truncated catalogue — keep the entry as-is rather than wrongly pruning it.
				next[idStr] = existing
			}
			// else: complete catalogue and bundle absent — drop the entry (prune)
		})

		return { ...state, redeemedBundles: next, bundleProducts, collectionWebPrices }
	})
	// Fills the same entry the catalogue would have, for the bundles it can never name. Contents included:
	// the redeemed entry fills its own copy from that catalogue, which for these holds nothing.
	.case(apiActions.fetchBundle.done, (state, payload): StoreState => {
		const bundle = payload.result
		if (bundle.id === undefined) {
			return state
		}

		return {
			...state,
			bundleProducts: {
				...state.bundleProducts,
				[bundle.id]: {
					active: bundle.pricing?.active ?? false,
					webPrice: bundle.pricing?.web_price,
					name: bundle.name,
					items: bundle.items,
				},
			},
			bundleFetchFailed: { ...state.bundleFetchFailed, [bundle.id]: undefined },
		}
	})
	// Counted and screened so the sync's replay cannot become a flood: a 4xx is never replayed, and anything
	// else only so many times. See handleMetaDataReadyForBundles.
	.case(apiActions.fetchBundle.failed, (state, payload): StoreState => {
		const previous = state.bundleFetchFailed?.[payload.params.bundleId]

		return {
			...state,
			bundleFetchFailed: {
				...state.bundleFetchFailed,
				[payload.params.bundleId]: {
					params: payload.params,
					attempts: (previous?.attempts ?? 0) + 1,
					permanent: isHttpClientError(payload.error),
				},
			},
		}
	})
